import { useState } from 'react';
import { AlertTriangle, ClipboardCheck } from 'lucide-react';
import { useComplianceSummary } from '../hooks/useCompliance';
import { FrameworkSummaryCard } from '../components/compliance/FrameworkSummaryCard';
import { ControlRow } from '../components/compliance/ControlRow';
import { Card } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';
import { getApiErrorMessage } from '../api/client';
import type { ComplianceFramework } from '../types';

export function Compliance() {
  const { data, isLoading, isError, error } = useComplianceSummary();
  const [activeFramework, setActiveFramework] = useState<ComplianceFramework>('cyber_essentials');

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Spinner size={28} />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Card className="p-8 text-center">
        <AlertTriangle className="mx-auto mb-3 text-rose-500" size={28} />
        <p className="font-medium text-slate-700 dark:text-slate-200">Couldn&apos;t load compliance data</p>
        <p className="mt-1 text-sm text-slate-400">{getApiErrorMessage(error)}</p>
      </Card>
    );
  }

  const controlsForFramework = data.controls.filter((c) => c.framework === activeFramework);
  const grouped = controlsForFramework.reduce<Record<string, typeof controlsForFramework>>((acc, c) => {
    (acc[c.category] ??= []).push(c);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-400">
        Tracks readiness and evidence, not certification - Cyber Essentials is self-assessed, Cyber Essentials Plus and
        ISO 27001 both need an independent certification body/auditor. See the Playbooks page for incident response
        procedures.
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {data.frameworks.map((summary) => (
          <FrameworkSummaryCard
            key={summary.framework}
            summary={summary}
            active={activeFramework === summary.framework}
            onClick={() => setActiveFramework(summary.framework)}
          />
        ))}
      </div>

      {controlsForFramework.length === 0 ? (
        <EmptyState icon={ClipboardCheck} title="No controls for this framework yet" />
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([category, controls]) => (
            <div key={category}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{category}</p>
              <div className="space-y-2">
                {controls.map((control) => (
                  <ControlRow key={control.id} control={control} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
